import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CustomerStatusBadge } from '@plato/ui-legacy/components/list-views/customer-status-badge';
import { Card } from '@plato/ui-legacy/components/ui/card';
import { InfoTag } from '@plato/ui-legacy/components/ui/info-tag';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { Store } from 'lucide-react';
import { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CustomerListItem } from '../../../server/queries/customer-query-many-with-filters.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { HomeRecommendationList } from '../../analytics/types/analytics-events.ts';
import { CustomerInfoTags } from '../../shared/components/customer-info-tags/customer-info-tags.tsx';

export type DashboardCustomerCardProps = {
    customer: CustomerListItem;
    /** Which "Recommended Customers" list the card is rendered in. */
    list: HomeRecommendationList;
    /** 1-based position of the card within its list. */
    position: number;
};

export const DashboardCustomerCard = forwardRef<HTMLDivElement, DashboardCustomerCardProps>(
    ({ customer, list, position }, ref) => {
        const { enablePlatoStatus } = useAppConfig();
        const navigate = useNavigate();
        const sendEvent = useAnalytics();

        return (
            <Card
                ref={ref}
                onClick={() => {
                    sendEvent('home_recommended_customer_opened', {
                        list,
                        customer_id: customer.customerId,
                        position,
                    });
                    void navigate(`/customers/${encodeURIComponent(customer.customerId)}`);
                }}
                className="p-3 w-full flex flex-col gap-2"
            >
                <div className="flex w-full items-center justify-between gap-2">
                    <InfoTag icon={Store} size="xs">
                        {customer.customerId}
                    </InfoTag>

                    {enablePlatoStatus && (
                        // h-0 keeps the taller badge from growing the row, so the gap below the
                        // ID matches the cards that have no badge
                        <div className="flex h-0 shrink-0 items-center">
                            <CustomerStatusBadge status={customer.platoStatus} />
                        </div>
                    )}
                </div>

                <Text weight="medium" color="neutral-900" className="text-sm">
                    {customer.customerName}
                </Text>

                <CustomerInfoTags customer={customer} maxTags={3} />
            </Card>
        );
    },
);

DashboardCustomerCard.displayName = 'DashboardCustomerCard';
